import {
    getUnitMarkupFromCapabilityVariable,
    getOrMakeUnitDefinitionFromMdsSiMarkup,
    getUnitDefinitionFromCfJsonForVariable,
    convertValueUnit,
} from '@metservicedev/cf-units';
import { Map } from 'immutable';
import _ from 'lodash';
import originalMoment from 'moment-timezone';
import { extendMoment } from 'moment-range';

import aggregateFunctions from '../../../utils/named-aggregate-functions';
import compareDatumWithLimit from '../../../utils/compareDatumWithLimit';
import convertNetcdfjsonToLineByLineDatum from '../../../utils/convertNetcdfjsonToLineByLineDatum';
import dataPoint from '../../../utils/dataPoint';
import latexElement from '../../../utils/latexElement';
import reduceDatasetsByViewblockState from '../../../utils/reduceDatasetsByViewblockState';
import roundNumber from '../../../utils/roundNumber';

const moment = extendMoment(originalMoment);

const getFirstTrippedThresholdOrNull = (warning, warningValue) => {
    const warningThreshold = warning.thresholds.find((threshold) => {
        const warningValueType = typeof warningValue;
        const limitValue = warningValueType === 'number' ? threshold.value : parseFloat(threshold.value);
        const isThresholdFound = compareDatumWithLimit({
            datum: parseFloat(warningValue),
            operator: threshold.comparator,
            limitValue,
        });
        return isThresholdFound;
    });
    return warningThreshold;
};

const mergeTemplateAndData = (state) => {
    const { viewblockState, time } = state;
    let { datasets } = state;
    const reportTime = time ? moment(time.iso8601).tz(time.timezone) : null;
    datasets = reduceDatasetsByViewblockState({ datasets, viewblockState });
    // New datability not yet loaded
    if (viewblockState.databilities.find((datability) => datability.datasetId == null)) {
        return state;
    }

    const databilities = viewblockState.databilities;
    const summariseWarnings = viewblockState.settings.summariseWarnings || false;
    const rowSize = viewblockState.settings.rowSize ? _.clamp(viewblockState.settings.rowSize, 1, 4) : 3;
    const fontSize = viewblockState.settings.fontSize ? viewblockState.settings.fontSize : 100;
    const blockBackgroundColor = viewblockState.settings.blockBackgroundColor ? viewblockState.settings.blockBackgroundColor : false;

    let warnings = viewblockState.settings.warnings ? _.cloneDeep(viewblockState.settings.warnings) : [];

    datasets = datasets.map((dataset) => {
        if (dataset.isError) {
            return null;
        }
        const result = convertNetcdfjsonToLineByLineDatum({ netcdfjson: dataset.cfjson });
        dataset.data = result;
        return dataset;
    });

    const warningsCompletedData = warnings.map((warning) => {
        const wholeDatability = databilities.find((d) => d.id == warning.databilityId);
        const dataset = wholeDatability
            ? datasets.find((d) => {
                  return d && wholeDatability.capabilityId === d.capabilityId;
              })
            : null;

        if (dataset) {
            const wholeCapability = dataset && dataset.capability ? dataset.capability : null;
            const variable = !!wholeCapability && Object.keys(wholeCapability.variables)[0];
            const sourceUnit = dataset.data && variable ? getUnitDefinitionFromCfJsonForVariable(dataset.cfjson, variable).unit : null;
            let capWholeUnit, capUnit;
            const capLatexUnit = getUnitMarkupFromCapabilityVariable(wholeCapability);

            if (capLatexUnit) {
                capWholeUnit = getOrMakeUnitDefinitionFromMdsSiMarkup(capLatexUnit);
            }

            if (capWholeUnit) {
                capUnit = capWholeUnit.unit;
            }

            const targetUnit = warning.unit || sourceUnit || capUnit;

            warning.unit = targetUnit;
            warning.latexUnit = (targetUnit && getOrMakeUnitDefinitionFromMdsSiMarkup(targetUnit).niceLatex) || '';
            warning.unitLabel = warning.unitLabel === warning.unit ? latexElement(warning.latexUnit) : latexElement(warning.unitLabel);
            warning.capability = wholeCapability;
            warning.fallbackLabel = wholeDatability.capabilityId;

            const addDurationMins = !(typeof warning.alert.periodMinutes === 'undefined') ? warning.alert.periodMinutes : 24 * 60;
            const range = moment.range([reportTime, reportTime.clone().add(addDurationMins, 'minutes')]);
            const dataInWarningTimeRange = dataset.data
                ? dataset.data.filter((datum) => {
                      return datum && datum.time && range.contains(datum.time);
                  })
                : [];

            warning.datums = dataInWarningTimeRange.map((datum) => {
                if (!dataPoint(datum, warning.capability)) return { time: datum.time, value: null };
                return {
                    value: convertValueUnit({ from: sourceUnit, to: targetUnit, value: dataPoint(datum, warning.capability) }),
                    time: datum.time,
                };
            });
            // Update the threshold values too?
            // const warningThresholds = viewblockState.settings.thresholds.filter((threshold) => threshold.warningId === warning.id);
            // if (warningThresholds.length > 0) {
            // 	const newThresholdsValue = warningThresholds.map((threshold) => {
            // 		return {
            // 			...threshold,
            // 			value: convertValueUnit({ from: sourceUnit, to: targetUnit, value: threshold.value }),
            // 		};
            // 	});
            // }

            // Calculate summary value
            const valuesOnly = warning.datums.map((data) => data.value);
            if (warning.alert.aggregate === 'quantile' && typeof warning.alert.percentile === 'undefined') {
                console.error('aggregate method quantile was specified, but no value was given');
                // throw 'broken contract error'
                return;
            }
            const summaryArgs = warning.alert.aggregate === 'quantile' ? [valuesOnly, warning.alert.percentile] : [valuesOnly];
            const summaryFunc = aggregateFunctions[warning.alert.aggregate] ? aggregateFunctions[warning.alert.aggregate].f : null;
            warning.summaryValue = NaN; // Default
            if (summaryArgs[0] && summaryArgs[0].length && summaryFunc) {
                warning.summaryValue = parseFloat(
                    aggregateFunctions[warning.alert.aggregate]['f'](
                        ...summaryArgs.filter((relevantDatum) => !(relevantDatum === null || relevantDatum === undefined))
                    )
                );
            }
            if (isNaN(warning.summaryValue) || warning.summaryValue === null || warning.summaryValue === undefined) {
                warning.summaryValue = '---'; // Default for display, can we change this to No Data?
            }
            warning.thresholds = (viewblockState.settings.thresholds || []).filter((t) => t.warningId === warning.id);
            return warning;
        }
        warning.thresholds = (viewblockState.settings.thresholds || []).filter((t) => t.warningId === warning.id);
        return warning;
    });

    const warningBoxData = warningsCompletedData.map((warning) => {
        const warningValue = warning.summaryValue === '---' ? warning.summaryValue : roundNumber({ num: warning.summaryValue, dp: warning.dp });

        return {
            id: warning.id,
            unclear: !warning.capability,
            label: warning.label ? warning.label : warning.capability ? warning.capability.display_name_short : warning.fallbackLabel,
            latexUnit: warning.latexUnit || warning.unit,
            unit: warning.unit,
            unitLabel: warning.unitLabel || warning.unit,
            threshold: getFirstTrippedThresholdOrNull(warning, warningValue),
            duration: warning.alert.periodMinutes,
            value: warning.summaryValue, // Leave unrounded and numeric, so can be used for numerical comparisons (thresholds)
            displayValue: warning.summaryValue ? warningValue : NaN || warning.summaryValue,
            aggregateLabel: warning.alert.label
                ? warning.alert.label
                : aggregateFunctions[warning.alert.aggregate]
                  ? aggregateFunctions[warning.alert.aggregate].suggestedLabel
                  : '',
        };
    });

    let { model } = state || Map({});

    model = {
        fontSize,
        summariseWarnings,
        blockBackgroundColor,
        rowSize,
        datums: warningBoxData,
    };

    return { ...state, model };
};

export default mergeTemplateAndData;
